import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Button, Form, Card, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faTwitter, faLinkedin, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { blogPosts } from '../blogData';
import { getFirestore, collection, addDoc, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { db, auth } from '../firebase';

interface Comment {
  id?: string;
  text: string;
  userName: string;
  createdAt: {
    toDate: () => Date;
  };
}

const BlogPostPage = () => {
  const { id } = useParams<{ id: string }>();
  const post = blogPosts.find((p) => p.id === parseInt(id || ''));
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!id) return;
    const commentsColRef = collection(db, 'comments');
    const q = query(commentsColRef, where('postId', '==', id), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedComments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Comment));
      setComments(fetchedComments);
    }, (err) => {
      console.error("Error fetching comments:", err);
      setError("Failed to load comments.");
    });

    return () => unsubscribe();
  }, [id]);

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError("You must be logged in to comment.");
      return;
    }
    if (newComment.trim() === '') return;

    setLoading(true);
    setError(null);

    try {
      await addDoc(collection(db, 'comments'), {
        postId: id,
        text: newComment,
        userName: user.email, // Using email as displayName might not be set
        createdAt: new Date(),
      });
      setNewComment('');
    } catch (err) {
      console.error("Error adding comment:", err);
      setError("Failed to post comment.");
    } finally {
      setLoading(false);
    }
  };

  if (!post) {
    return <div>Post not found</div>;
  }

  return (
    <Container className="py-5">
      <h2>{post.title}</h2>
      <p>{post.content}</p>

      <div className="mt-4">
        <h5>Share this post:</h5>
        <Button variant="outline-primary" className="me-2" href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank">
          <FontAwesomeIcon icon={faFacebook} /> Facebook
        </Button>
        <Button variant="outline-info" className="me-2" href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(post.title)}`} target="_blank">
          <FontAwesomeIcon icon={faTwitter} /> Twitter
        </Button>
        <Button variant="outline-primary" className="me-2" href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${encodeURIComponent(post.title)}`} target="_blank">
          <FontAwesomeIcon icon={faLinkedin} /> LinkedIn
        </Button>
        <Button variant="outline-success" href={`https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + " " + window.location.href)}`} target="_blank">
          <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
        </Button>
      </div>

      <hr className="my-5" />

      <div>
        <h4>Comments</h4>
        <Card className="mb-4">
          <Card.Body>
            {user ? (
              <Form onSubmit={handleCommentSubmit}>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    required
                  />
                </Form.Group>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Posting...' : 'Post Comment'}
                </Button>
              </Form>
            ) : (
              <Alert variant="info">
                Please <a href="/cashbook/index.html">log in</a> to post a comment.
              </Alert>
            )}
          </Card.Body>
        </Card>

        {comments.map((comment) => (
          <Card key={comment.id} className="mb-3">
            <Card.Body>
              <Card.Subtitle className="mb-2 text-muted">
                <strong>{comment.userName}</strong> on {comment.createdAt.toDate().toLocaleDateString()}
              </Card.Subtitle>
              <Card.Text>{comment.text}</Card.Text>
            </Card.Body>
          </Card>
        ))}
        {comments.length === 0 && !error && <p>No comments yet. Be the first to comment!</p>}
      </div>
    </Container>
  );
};

export default BlogPostPage;